/**
 * F 392 — the `/database` slash-menu suggestions.
 *
 * BlockNote's default slash menu is built by `getDefaultReactSlashMenuItems(editor)`.
 * FVC's ask is "type /database, then pick Quick Notes", so this cannot be ONE
 * static item: it is one entry per database in the graph, each inserting the
 * `inlineDatabase` block with that database's id.
 *
 * Shapes measured in the installed packages (0.47.3), not assumed:
 *  - `DefaultSuggestionItem` = `{ key, title, onItemClick, subtext?, badge?, aliases?, group? }`
 *    (node_modules/@blocknote/core/src/extensions/SuggestionMenu/DefaultSuggestionItem.ts:3)
 *  - `key` is typed `keyof Omit<Dictionary['slash_menu'],'page_break'>`, so a
 *    custom key needs the `Omit<…, 'key'> & { key: '…' }` widening the shipped
 *    PageBreak item uses (core/src/blocks/PageBreak/getPageBreakSlashMenuItems.ts:31).
 *  - the built-in items insert via `insertOrUpdateBlockForSlashMenu`, but
 *    this block does NOT — see the comment at the insertion site for the
 *    measured reason (`content: 'none'` + updateBlock = no cursor position).
 *  - `blockSpecs` replaces rather than merges, and `editor.schema.blockSchema`
 *    is how the PageBreak item feature-detects its own block.
 */

import type { BlockNoteEditor, BlockSchema, InlineContentSchema, StyleSchema } from '@blocknote/core';
import type { Database } from '../../../types/models/Database';
import { loadDatabases } from '../../../core/workspace/databaseStorage';

/** One slash-menu row per database. */
export type InlineDatabaseSuggestion = Omit<
  import('@blocknote/core/extensions').DefaultSuggestionItem,
  'key'
> & { key: string };

/** Feature-detect: is the block in the editor that is actually mounted? */
export function hasInlineDatabaseBlock(
  editor: BlockNoteEditor<any, any, any>,
): boolean {
  try {
    return 'inlineDatabase' in (editor as unknown as { schema: { blockSchema: object } }).schema
      .blockSchema;
  } catch {
    return false;
  }
}

/**
 * Build the suggestions for the databases currently in the graph.
 *
 * An empty graph yields `[]`, and the slash menu then shows no database
 * entries — which is correct: there is nothing to embed. Never a hardcoded
 * placeholder name, which would be a block pointing at an id that never
 * resolves.
 */
export function getInlineDatabaseSlashMenuItems<
  BSchema extends BlockSchema,
  I extends InlineContentSchema,
  S extends StyleSchema,
>(editor: BlockNoteEditor<BSchema, I, S>): InlineDatabaseSuggestion[] {
  if (!hasInlineDatabaseBlock(editor)) return [];

  let databases: Record<string, Database> = {};
  try {
    databases = loadDatabases();
  } catch {
    // A storage failure must not take the editor down; the menu simply offers
    // nothing and the block itself renders a visible "missing" state.
    return [];
  }

  return Object.values(databases ?? {})
    .filter((db) => db && db.id)
    .sort((a, b) => String(a.name ?? '').localeCompare(String(b.name ?? '')))
    .map((db) => {
      const count = db.items?.length ?? 0;
      const id = db.id;
      const label = db.name || id;
      return {
        // A unique key per database, so the menu does not collapse rows when
        // two databases share a display name.
        key: `inline_database:${id}`,
        title: `Database — ${label}`,
        subtext: `${count} ${count === 1 ? 'item' : 'items'}`,
        aliases: ['database', 'inline database', 'embed', label, id],
        group: 'Databases',
        onItemClick: (passedEditor?: unknown) => {
          // THE EDITOR MUST COME FROM THE ARGUMENT.
          //
          // Measured in @blocknote/react's SuggestionMenuController (0.47.3,
          // src/components/SuggestionMenu/SuggestionMenuController.tsx): the
          // default click handler is
          //     item.onItemClick(editor)
          // where `editor` is the controller's OWN `useBlockNoteEditor()`.
          // An item that closes over a DIFFERENT editor instance — e.g. one
          // captured in a `useMemo` at PageEditor render time, before the
          // view attached — inserts into an editor that is not the one in the
          // DOM. Measured symptom: the menu renders, the row is clickable,
          // NOTHING is inserted, and there is no error anywhere. Every item
          // is affected, built-ins included, which is why this looked like
          // "the whole slash menu is broken".
          const ed = (passedEditor ?? editor) as BlockNoteEditor<BSchema, I, S>;
          const current = ed.getTextCursorPosition().block;
          ed.insertBlocks(
            [{ type: 'inlineDatabase', props: { databaseId: id } } as never],
            current,
            'after',
          );
          ed.setTextCursorPosition(current, 'end');
        },
      } as InlineDatabaseSuggestion;
    });
}

export default getInlineDatabaseSlashMenuItems;